import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { batch, useDispatch, useSelector } from 'react-redux';
import { openPopupWidget } from 'react-calendly';
import ResizeObserver from 'react-resize-observer';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import Shimmer from 'react-shimmer-effect';

import PropTypes from 'prop-types';
import corePackage, {
  includedModules,
  getFilteredCoreModules,
  addOns,
  arePackagesEqual,
  updatePackages,
} from './components/presentational/packages';
import amsBannerLocked from '../../shared/images/ss-hub/ams-banner-locked.png';
import amsBannerUnlocked from '../../shared/images/ss-hub/ams-banner-unlocked.png';
import ModuleCard from './components/presentational/ModuleCard';
import ModuleCardLoading from './components/presentational/ModuleCardLoading';
import {
  fetchSubscriptionPackage,
  setIsInstantPaymentModalShowing,
  setIsSideBarExpandedGlobal,
  setIsUpdatedUpgradeModalShowing,
  setIsUpgradeModalShowing,
  setSelectedPackage,
} from './ducks/sportsScienceActions';
import {
  subscriptionFlowTracker,
  subscriptionFlowTrackerDemo,
} from '../../shared/utils/amplitudeHelper';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const MainContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 44px;
  width: 100%;
  @media screen and (max-width: 767px) {
    margin-bottom: 44px;
  }
`;

const InfoBanner = styled('div')`
  width: calc(100% - 88px);
  height: auto;
  margin: 44px auto 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  padding: 12px 40px;
  background-image: linear-gradient(to right, #1e212e, transparent),
    url(${(props) => (props.unlocked ? amsBannerUnlocked : amsBannerLocked)});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  color: #ffffff;
  @media screen and (max-width: 714px) {
    width: 100%;
    margin: 0;
    border-radius: 0;
  }
`;

const BannerContent = styled('div')`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  min-height: 128px;
  gap: 20px;
  @media screen and (max-width: 714px) {
    min-height: 157px;
  }
`;

const BannerHeader = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: 'Nunito Sans';
  .banner-title {
    font-size: 32px;
    line-height: normal;
    letter-spacing: ${(props) => (props.unlocked ? '0.72px' : '-0.444px')};
    font-weight: 900;
  }
  @media screen and (max-width: 714px) {
    .banner-title {
      font-size: 23px;
      font-weight: 700;
      line-height: 130%;
      letter-spacing: -0.23px;
    }
  }
`;

const BannerButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
`;

const ScheduleDemoButton = styled('button')`
  background: linear-gradient(270deg, #fc4a1a 0%, #f7b733 100%);
  color: #ffffff;
  border-radius: 10000px;
  box-shadow: 0px 2px 9px 0px rgba(0, 0, 0, 0.12);
  border: none;
  height: 40px;
  min-width: 64px;
  padding: 0px 20px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 900;
  line-height: 16px;
  letter-spacing: 0.39px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 auto;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const ThatsTeamBuildrText = styled('div')`
  display: flex;
  min-height: 40px;
  padding: 0 8px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  background: #f60;
  color: var(--theme-fg-on-accent, #fff);
  font-family: 'Nunito Sans';
  font-size: 32px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  width: fit-content;
  @media screen and (max-width: 714px) {
    font-size: 23px;
  }
`;

const ShimmerInfoBanner = styled('div')`
  width: calc(100% - 88px);
  min-height: 128px;
  margin: 44px auto 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  padding: 12px 40px;
  background: #1e212e;
  border-radius: 12px;
  @media screen and (max-width: 714px) {
    width: 100%;
    height: 157px;
    margin: 0;
    border-radius: 0;
  }
`;

const CardSection = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const SectionHeaderAndDescription = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardSectionHeader = styled('div')`
  display: flex;
  flex-direction: ${(props) => (props.purchaseButton && props.containerWidth < 560 ? 'column' : 'row')};
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  .section-title {
    font-feature-settings: 'liga' off, 'clig' off;
    color: #444444;
    font-family: 'Nunito Sans';
    font-size: 23px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 29.9px */
    letter-spacing: -0.23px;
    margin-bottom: ${(props) => (props.purchaseButton && props.containerWidth < 560 ? '12px' : '0')};
    margin-right: ${(props) => (props.purchaseButton && props.containerWidth >= 560 ? '32px' : '0')};
  }
  @media screen and (max-width: 714px) {
    justify-content: center;
  }
`;

const ShimmerSectionHeader = styled('div')`
  width: 150px;
  height: 30px;
  border-radius: 8px;
`;

const PurchasePackageButton = styled('button')`
  background: ${(props) => props.bgColor || '#444444'};
  display: flex;
  padding: 12px 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 10000px;
  box-shadow: 0px 2px 9px 0px rgba(0, 0, 0, 0.12);
  border: none;
  color: #ffffff;
  font-family: 'Nunito Sans';
  font-weight: 900;
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const CardSectionDescription = styled('div')`
  display: flex;
  padding: 12px;
  gap: 32px;
  align-self: stretch;
  align-items: center;
  font-family: 'Nunito Sans';
  font-size: 19px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 28.5px */
  font-feature-settings: 'liga' off, 'clig' off;
  color: #444444;
  width: fit-content;
  background-color: #ffffff;
  padding: 12px;
  border-radius: 8px;
`;

const ModuleCards = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  align-self: stretch;
  gap: 20px;
  flex-wrap: wrap;
  @media screen and (max-width: 714px) {
    justify-content: center;
  }
`;

const SectionDivider = styled('div')`
  width: 100%;
  height: 2px;
  align-self: stretch;
  border-radius: 10000px;
  background: #e0e0e0;
  margin: 44px 0;
`;

const CardGroup = ({
  title,
  purchaseButton,
  purchaseClickFunction,
  gradient,
  description,
  cards,
  containerWidth,
  coreSubscriptionUnlocked,
  isStatusLoading,
}) => (
  <CardSection>
    <SectionHeaderAndDescription>
      {isStatusLoading ? (
        <Shimmer>
          <ShimmerSectionHeader />
        </Shimmer>
      ) : (
        <CardSectionHeader
          containerWidth={containerWidth}
          purchaseButton={purchaseButton}
        >
          <div className='section-title'>{title}</div>
          {purchaseButton && (
            <PurchasePackageButton
              bgColor={gradient}
              onClick={purchaseClickFunction}
            >
              {purchaseButton}
            </PurchasePackageButton>
          )}
        </CardSectionHeader>
      )}

      {description && containerWidth > 700 && (
        <CardSectionDescription>{description}</CardSectionDescription>
      )}
    </SectionHeaderAndDescription>

    {cards.length > 0 && (
      <ModuleCards>
        {cards.map((card) => {
          if (isStatusLoading) {
            return <ModuleCardLoading key={card.id} />;
          }
          return (
            <ModuleCard
              key={card.id}
              cardData={card}
              purchaseButton={
                card.subscriptionName === 'Add On'
                && !card.unlocked
                && coreSubscriptionUnlocked
              }
              locked={!card.unlocked}
            />
          );
        })}
      </ModuleCards>
    )}
  </CardSection>
);

CardGroup.propTypes = {
  title: PropTypes.string.isRequired,
  purchaseButton: PropTypes.string,
  purchaseClickFunction: PropTypes.func,
  gradient: PropTypes.string,
  description: PropTypes.string,
  cards: PropTypes.instanceOf(Array).isRequired,
  containerWidth: PropTypes.number.isRequired,
  coreSubscriptionUnlocked: PropTypes.bool,
  isStatusLoading: PropTypes.bool,
};

CardGroup.defaultProps = {
  purchaseButton: '',
  purchaseClickFunction: () => {},
  gradient: '',
  description: '',
  coreSubscriptionUnlocked: false,
  isStatusLoading: false,
};

const SportScienceHome = ({ isSideBarActive, flags }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const subscriptionStatus = useSelector(
    (state) => state.sportsScience.data.subscriptionStatus,
  );
  const isStatusLoading = useSelector(
    (state) => state.sportsScience.ui.isStatusLoading,
  );
  const isSideBarOpen = useSelector(
    (state) => state.sportsScience.data.isSideBarOpen,
  );
  const isSideBarExpanded = useSelector(
    (state) => state.sportsScience.data.isSideBarExpanded,
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  // Determine Platinum Pro plan and entitlement flags for VBT visibility
  const organizationAccessLevel = currentUser?.organizationAccessLevel;
  const isPlatinumPro = organizationAccessLevel === 7;
  const vbtDashStandAloneEnabled = currentUser?.userOrgSSHubSettings?.vbtDashStandAloneEnabled;
  const shouldShowVbtOption = isPlatinumPro;

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (containerElement) {
      setTimeout(() => {
        setContainerWidth(containerElement.offsetWidth);
      }, 300);
    }
  }, [isSideBarOpen, isSideBarActive, isSideBarExpanded]);

  useEffect(() => {
    dispatch(setIsSideBarExpandedGlobal(false));
  }, []);

  const coreSubscriptionUnlocked = !!subscriptionStatus
    ?.packageSubscriptionStatus?.isSubscribed;

  // Check if user has active subscription for add-on with particular ID from array of add-ons
  const addOnUnlocked = (addOnModuleId) => subscriptionStatus?.addOnsSubscriptionStatus?.some(
    (addOn) => addOn.isSubscribed && addOn.addOnId === addOnModuleId,
  );

  // Modify module array based on active feature flags and manage unlocked property
  const moduleCardData = useMemo(() => {
    const coreModules = getFilteredCoreModules(flags).map((module) => ({
      ...module,
      unlocked: coreSubscriptionUnlocked,
    }));

    return [
      ...includedModules,
      ...coreModules,
      ...addOns.map((module) => ({
        ...module,
        unlocked: addOnUnlocked(module.addOnId)
          || (module.addOnId === 2 && vbtDashStandAloneEnabled === true),
      }))
        // Hide VBT card entirely unless eligible
        .filter((module) => module.addOnId !== 2 || shouldShowVbtOption),
    ];
  }, [
    coreSubscriptionUnlocked,
    subscriptionStatus?.addOnsSubscriptionStatus,
    flags,
    shouldShowVbtOption,
    vbtDashStandAloneEnabled,
  ]);

  // Translate URL path segment to module name
  function getModuleNameFromPathSegment(dashName) {
    let dash;
    switch (dashName) {
      case 'wearables-dashboard':
        dash = 'Wearables Dashboard';
        break;
      case 'load-monitoring-dashboard':
        dash = 'Load Monitoring Dashboard';
        break;
      case 'kpi-tracker':
        dash = 'KPI Tracker';
        break;
      case 'body-heatmap':
        dash = 'Body Heatmap';
        break;
      case 'habit-tracker':
        dash = 'Habit Tracker';
        break;
      case 'force-plate-dashboard':
        dash = 'Force Plate Dashboard';
        break;
      case 'volume-tracker':
        dash = 'Volume Tracker';
        break;
      case 'vbt-dashboard':
        dash = 'VBT Dashboard';
        break;
      default:
        dash = dashName;
        break;
    }
    return moduleCardData.find((module) => module.title === dash);
  }

  // If coach tries to visit locked dash,
  // Redirect to home page and show upgrade modal for that dash
  const handleOpenModalFromPrevious = (moduleData) => {
    subscriptionFlowTracker(
      'Modal Viewed',
      moduleData.legacyTitle || moduleData.title,
      'AMS Homepage',
    );
    batch(() => {
      dispatch(
        moduleData.title === 'Body Heatmap'
          || moduleData.title === 'Habit Tracker'
          || moduleData.title === 'Volume Tracker'
          || moduleData.title === 'VBT Dashboard'
          ? setIsUpdatedUpgradeModalShowing(true)
          : setIsUpgradeModalShowing(true),
      );
      dispatch(setSelectedPackage(moduleData));
      dispatch(
        fetchSubscriptionPackage(
          currentUser.accountCode,
          1,
          moduleData.addOnId || null,
        ),
      );
    });
  };

  // Read which module user was trying to visit before redirect, then delete that param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const previous = searchParams.get('previous');
    if (searchParams.has('previous')) {
      // If previous was VBT and VBT should not be visible, clear previous param
      if (previous === 'vbt-dashboard' && !shouldShowVbtOption) {
        searchParams.delete('previous');
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
        return;
      }
      const module = previous ? getModuleNameFromPathSegment(previous) : null;
      if (module) {
        handleOpenModalFromPrevious(module);
      }
      searchParams.delete('previous');
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [location.search, shouldShowVbtOption]);

  const [packages, setPackages] = useState([...moduleCardData]);

  const updatedPackages = useMemo(
    () => updatePackages(packages, subscriptionStatus, coreSubscriptionUnlocked),
    [subscriptionStatus, packages, coreSubscriptionUnlocked],
  );

  useEffect(() => {
    // Assuming arePackagesEqual is a deep comparison function
    if (!arePackagesEqual(updatedPackages, packages)) {
      setPackages(updatedPackages);
    }
  }, [updatedPackages]);

  const unlockedModules = moduleCardData.filter((module) => module.unlocked);

  const coreSectionModules = moduleCardData.filter((module) => !module.addOnId);

  const addOnModules = moduleCardData.filter(
    (module) => module.subscriptionName === 'Add On',
  );
  const lockedAddOnModules = addOnModules.filter((module) => !module.unlocked);

  // Direct to Instant Payment Modal to purchase core subscription
  const handlePurchasePackageClick = () => {
    batch(() => {
      dispatch(setSelectedPackage(corePackage));
      dispatch(fetchSubscriptionPackage(currentUser.accountCode, 1));
      dispatch(setIsInstantPaymentModalShowing(true));
    });
    subscriptionFlowTracker(
      'Purchase Package Button Clicked',
      'Upgrade Modal',
      'AMS Homepage',
    );
  };

  const handleAmplitudeDemo = () => {
    const packageData = {
      title: 'View All Modules',
      addOn: false,
      core: false,
    };
    subscriptionFlowTrackerDemo('AMS Homepage Banner', packageData);
  };

  return (
    <Container ref={containerRef}>
      <ResizeObserver
        onResize={(rect) => {
          setContainerWidth(rect?.width);
        }}
      />
      {isStatusLoading ? (
        <Shimmer>
          <ShimmerInfoBanner />
        </Shimmer>
      ) : (
        <InfoBanner unlocked={coreSubscriptionUnlocked}>
          <BannerContent>
            <BannerHeader
              unlocked={coreSubscriptionUnlocked}
              containerWidth={containerWidth}
            >
              <div className='banner-title'>
                {coreSubscriptionUnlocked
                  ? 'Let Us Help You Make the Most of Your Data'
                  : 'Fewer Clicks, More Coaching'}
              </div>
            </BannerHeader>
            <BannerButtonContainer>
              {coreSubscriptionUnlocked ? (
                <ScheduleDemoButton
                  onClick={() => {
                    handleAmplitudeDemo();
                    openPopupWidget({
                      url:
                        'https://calendly.com/teambuildr/15-minute-sports-science-hub',
                    });
                  }}
                >
                  SCHEDULE A DEMO
                </ScheduleDemoButton>
              ) : (
                <ThatsTeamBuildrText>
                  That&apos;s TeamBuildr AMS.
                </ThatsTeamBuildrText>
              )}
            </BannerButtonContainer>
          </BannerContent>
        </InfoBanner>
      )}

      <MainContainer>
        {coreSubscriptionUnlocked ? (
          <CardGroup
            title='Your modules'
            cards={unlockedModules}
            containerWidth={containerWidth}
            coreSubscriptionUnlocked={coreSubscriptionUnlocked}
            isStatusLoading={isStatusLoading}
          />
        ) : (
          <CardGroup
            title='Core subscription'
            cards={coreSectionModules}
            purchaseButton='PURCHASE CORE SUBSCRIPTION'
            purchaseClickFunction={handlePurchasePackageClick}
            gradient='linear-gradient(270deg, #FC4A1A 0%, #F7B733 100%)'
            containerWidth={containerWidth}
            coreSubscriptionUnlocked={coreSubscriptionUnlocked}
            isStatusLoading={isStatusLoading}
          />
        )}
        {!!lockedAddOnModules.length && (
          <>
            <SectionDivider />
            <CardGroup
              title='Add ons'
              cards={lockedAddOnModules}
              gradient='linear-gradient(270deg, #6A82FB 0%, #FC5C7D 100%)'
              containerWidth={containerWidth}
              coreSubscriptionUnlocked={coreSubscriptionUnlocked}
              isStatusLoading={isStatusLoading}
            />
          </>
        )}
      </MainContainer>
    </Container>
  );
};

SportScienceHome.propTypes = {
  isSideBarActive: PropTypes.bool.isRequired,
  flags: PropTypes.instanceOf(Object).isRequired,
};

export default withLDConsumer()(SportScienceHome);
